import type { UserFormInputFieldType } from "@/types/user-form";
import type { PersistedCartUser } from "@/api/persistentCartApi";

const toTrimmedString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const toOptionalBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const legacyConfirmationFields = [
  "businessCustomerConfirmed",
  "termsAccepted",
  "reviewSubmissionAuthorized",
  "successBasedBillingAccepted",
] as const;

export const checkoutConfirmedFromUser = (
  value: Record<string, unknown>,
): boolean => value.checkout_confirmed === true;

export const normalizePersistedCheckoutUser = (
  value: object | undefined,
): UserFormInputFieldType => {
  const user = { ...(value as Record<string, unknown> | undefined) };
  const checkoutConfirmed = checkoutConfirmedFromUser(user);

  for (const field of legacyConfirmationFields) {
    delete user[field];
  }

  user.checkout_confirmed = checkoutConfirmed;

  return user as UserFormInputFieldType;
};

export type CheckoutUserDraft = {
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  salutation: string;
  firma: string;
  location: string;
  plz: string;
  street: string;
  newsletterOptIn?: boolean;
  checkout_confirmed: boolean;
};

export const checkoutUserDraftFromFormValue = (value: {
  firstName?: unknown;
  surname?: unknown;
  email?: unknown;
  phone?: unknown;
  salutation?: unknown;
  firma?: unknown;
  plz?: unknown;
  location?: unknown;
  street?: unknown;
  newsletterOptIn?: unknown;
  checkout_confirmed?: unknown;
}): CheckoutUserDraft => ({
  firstName: toTrimmedString(value.firstName),
  surname: toTrimmedString(value.surname),
  email: toTrimmedString(value.email),
  phone: toTrimmedString(value.phone),
  salutation: toTrimmedString(value.salutation),
  firma: toTrimmedString(value.firma),
  plz: toTrimmedString(value.plz),
  location: toTrimmedString(value.location),
  street: toTrimmedString(value.street),
  newsletterOptIn: toOptionalBoolean(value.newsletterOptIn),
  checkout_confirmed: checkoutConfirmedFromUser(
    value as Record<string, unknown>,
  ),
});

export const checkoutUserDraftHasContact = (user: CheckoutUserDraft): boolean =>
  Boolean(user.firstName || user.surname || user.email || user.phone);

export const checkoutUserDraftDisplayName = (user: CheckoutUserDraft): string =>
  [user.firstName, user.surname].filter(Boolean).join(" ");

export const toPersistedCartUser = (
  user: CheckoutUserDraft,
): PersistedCartUser => ({
  firstName: user.firstName,
  surname: user.surname,
  email: user.email,
  phone: user.phone,
  salutation: user.salutation,
  firma: user.firma,
  location: user.location,
  plz: user.plz,
  street: user.street,
  ...(user.newsletterOptIn !== undefined
    ? { newsletterOptIn: user.newsletterOptIn }
    : {}),
  checkout_confirmed: user.checkout_confirmed,
  businessCustomerConfirmed: user.checkout_confirmed,
  termsAccepted: user.checkout_confirmed,
  reviewSubmissionAuthorized: user.checkout_confirmed,
  successBasedBillingAccepted: user.checkout_confirmed,
});

export const mergePersistedCartUser = (
  existing: object | undefined,
  draft: CheckoutUserDraft,
): PersistedCartUser => ({
  ...normalizePersistedCheckoutUser(existing),
  ...toPersistedCartUser(draft),
});
